import { useCallback, useEffect, useState } from 'react';

/**
 * Data loading, deliberately small.
 *
 * `reload` is what makes an operator console feel truthful: every mutating action calls it
 * so the screen shows what the API now says, rather than what the UI hoped it would say.
 */
export interface Loaded<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // The dependency list is the caller's: `load` is a fresh closure on every render, so
    // depending on it would re-fetch forever. Callers pass what actually identifies the
    // request (a resource id, a filter), and `nonce` is what `reload` bumps.
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}

/** Re-runs a loader on an interval. Used by the pages that watch live state. */
export function usePolling(reload: () => void, intervalMs: number, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(reload, intervalMs);
    return () => clearInterval(timer);
  }, [reload, intervalMs, enabled]);
}
